import { ReactElement } from "react";
import { PageContainer, PageTitle, SkeletonLoader, OfferCard } from "../../global/Common";
import { Box, Card, CardContent, Typography, Button, useTheme, useMediaQuery } from "@mui/material";
import { useUserData } from "../../context/UserDataContext";
import { useLanguage } from "../../context/LanguageContext";
import { useBrand } from "../../context/BrandContext";
import OfferBarcodeDialog from "./OfferBarcodeDialog";
import { OfferGrid } from "./OfferGrid";
import { useOffers } from "./useOffers";
import { getOfferImage } from "./utils";

const MesOffres = (): ReactElement => {
  const { t } = useLanguage();
  const { personData, loading } = useUserData();
  const { brand } = useBrand();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const offers = personData?.OffersResponse?.GetOffersResponses || [];
  
  const {
    availableOffers,
    expiredOffers,
    selectedOffer,
    showExpiredOffers,
    isExpanding,
    isCollapsing,
    handleExpand,
    handleCollapse,
    handleSelectOffer,
    handleCloseDialog,
  } = useOffers(offers);

  return (
    <PageContainer>
      <PageTitle>{t('pages.offers.title')}</PageTitle>
      
      <Box sx={{ mt: 3 }}>
        {loading ? (
          <OfferGrid>
            {[1, 2, 3].map((item) => (
              <Card key={item}>
                <CardContent>
                  <SkeletonLoader count={4} />
                </CardContent>
              </Card>
            ))}
          </OfferGrid>
        ) : offers.length > 0 ? (
          <>
            {(availableOffers.length > 0 || (!isMobile && expiredOffers.length > 0)) && (
              <OfferGrid mb={4}>
                {availableOffers.map((offer, index) => (
                  <OfferCard 
                    key={index}
                    offer={offer}
                    onClick={() => handleSelectOffer(offer, t('pages.offers.noTitle'))}
                    showDescription
                  />
                ))}
                {!isMobile && expiredOffers.length > 0 && (
                  <Box sx={{ position: 'relative' }}>
                    <OfferCard 
                      offer={expiredOffers[0]}
                      onClick={() => handleSelectOffer(expiredOffers[0], t('pages.offers.noTitle'))}
                      showDescription
                    />
                    {!showExpiredOffers && (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExpand();
                        }}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          },
                        }}
                      >
                        <Button
                          variant="contained"
                          size="large"
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            backgroundColor: brand.primaryColor,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              backgroundColor: brand.primaryColor,
                              transform: 'scale(1.05)',
                            }
                          }}
                        >
                          Afficher mes offres dépassées
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </OfferGrid>
            )}

            {expiredOffers.length > 0 && (
              <Box>
                {isMobile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: showExpiredOffers ? 2 : 0, mb: 3, alignItems: 'center', position: 'relative' }}>
                    {expiredOffers.slice(0, 3).map((offer, index) => {
                      const scale = 1 - (index * 0.08);
                      const marginTop = index === 1 ? -60 : index * -30;
                      const overlayOpacity = index * 0.15;
                      const isFirst = index === 0;
                      
                      return (
                        <Box 
                          key={index} 
                          sx={{ 
                            marginTop: showExpiredOffers ? 0 : (index === 0 ? 0 : `${marginTop}px`),
                            position: 'relative',
                            zIndex: showExpiredOffers ? 'auto' : (3 - index),
                            width: '100%',
                            transform: showExpiredOffers ? 'scale(1)' : `scale(${scale})`,
                            transformOrigin: 'top center',
                            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            opacity: showExpiredOffers ? 0.5 : 1,
                          }}
                        >
                        <Box 
                          sx={{ 
                            position: 'relative',
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: showExpiredOffers ? 'transparent' : `rgba(0, 0, 0, ${overlayOpacity})`,
                              pointerEvents: 'none',
                              zIndex: 1,
                              transition: 'background-color 0.8s ease',
                            }
                          }}
                        >
                          <Box sx={{ position: 'relative', zIndex: 0 }}>
                            <OfferCard 
                              offer={offer}
                              onClick={() => showExpiredOffers ? handleSelectOffer(offer, t('pages.offers.noTitle')) : undefined}
                              showDescription
                            />
                          </Box>
                        </Box>

                        {isFirst && !showExpiredOffers && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(0, 0, 0, 0.4)',
                              backdropFilter: 'blur(2px)',
                              borderRadius: 1,
                              zIndex: 10,
                              cursor: 'pointer',
                              transition: 'background-color 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              }
                            }}
                            onClick={handleExpand}
                          >
                            <Button
                              variant="contained"
                              size="large"
                              sx={{
                                fontSize: '1rem',
                                fontWeight: 600,
                                px: 3,
                                py: 1.2,
                                textTransform: 'none',
                                backgroundColor: brand.primaryColor,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                transition: 'transform 0.2s ease',
                                '&:hover': {
                                  backgroundColor: brand.primaryColor,
                                  transform: 'scale(1.05)',
                                }
                              }}
                            >
                              Afficher mes offres dépassées
                            </Button>
                          </Box>
                        )}
                      </Box>
                    );
                    })}
                  </Box>
                ) : null}

                {isMobile && expiredOffers.length > 3 && showExpiredOffers && (
                  <OfferGrid mb={2}>
                    {expiredOffers.slice(3).map((offer, index) => (
                      <Box 
                        key={index + 3} 
                        sx={{ 
                          opacity: 0.5,
                          animation: `slideInCard 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${(index + 3) * 0.05}s both`,
                          '@keyframes slideInCard': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(20px)',
                            },
                            '100%': {
                              opacity: 0.5,
                              transform: 'translateY(0)',
                            },
                          },
                        }}
                      >
                        <OfferCard 
                          offer={offer}
                          onClick={() => handleSelectOffer(offer, t('pages.offers.noTitle'))}
                          showDescription
                        />
                      </Box>
                    ))}
                  </OfferGrid>
                )}

                {!isMobile && showExpiredOffers && (
                  <OfferGrid mb={2}>
                    {expiredOffers.slice(1).map((offer, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          opacity: 0.5,
                          animation: isCollapsing 
                            ? `slideOutCardReverse 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s both`
                            : `slideInCardReverse 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s both`,
                          '@keyframes slideInCardReverse': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(-20px) scale(0.95)',
                            },
                            '100%': {
                              opacity: 0.5,
                              transform: 'translateY(0) scale(1)',
                            },
                          },
                          '@keyframes slideOutCardReverse': {
                            '0%': {
                              opacity: 0.5,
                              transform: 'translateY(0) scale(1)',
                            },
                            '100%': {
                              opacity: 0,
                              transform: 'translateY(-20px) scale(0.95)',
                            },
                          },
                        }}
                      >
                        <OfferCard 
                          offer={offer}
                          onClick={() => handleSelectOffer(offer, t('pages.offers.noTitle'))}
                          showDescription
                        />
                      </Box>
                    ))}
                  </OfferGrid>
                )}

                {showExpiredOffers && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCollapse}
                      sx={{ 
                        borderColor: brand.primaryColor,
                        color: brand.primaryColor,
                        animation: 'fadeIn 0.4s ease 0.3s both',
                        '&:hover': {
                          borderColor: brand.primaryColor,
                          backgroundColor: `${brand.primaryColor}10`,
                        },
                        '@keyframes fadeIn': {
                          '0%': { opacity: 0 },
                          '100%': { opacity: 1 },
                        },
                      }}
                    >
                      Masquer les offres dépassées
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </>
        ) : (
          <Card>
            <CardContent>
              <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                {t('pages.offers.noOffers')}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      <OfferBarcodeDialog 
        open={!!selectedOffer}
        onClose={handleCloseDialog}
        offerNumber={selectedOffer?.offerNumber || ''}
        offerName={selectedOffer?.offerName || ''}
        endDate={selectedOffer?.endDate}
        offerImage={selectedOffer ? getOfferImage(selectedOffer.offerNumber) : undefined}
      />
    </PageContainer>
  );
};

export default MesOffres;